import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Textarea} from '@/components/ui/textarea'
import {Campo} from '@/components/campo'
import {NativeSelect} from '@/components/native-select'
import {SegmentedControl} from '@/components/segmented-control'
import {ListEmptyState} from '@/components/section-card'
import {EspinaDePescado, type EspinaData} from '@/components/analisis/espina-de-pescado'
import {useCategorias} from '@/hooks/sgsst/incidentes/use-investigacion'
import {
  useAbrirAnalisis,
  useAgregarCausa,
  useAnalisisCausa,
  useCambiarMetodologia,
  useDescartarAnalisis,
  useQuitarCausa,
  type AnalisisCausa,
  type AnalisisNodo,
  type CausaCategoria,
  type Metodologia,
} from '@/hooks/sgsst/plan/use-analisis-causa'
import {translateApiError} from '@/lib/errors'
import {tope} from '@/lib/campos-texto'

const METODOLOGIAS: Metodologia[] = ['ishikawa', 'arbol_causas', 'cinco_porques']
const SIN_ESPINA = 'sin'

/** En el orden del catálogo (el mismo del motor del backend), «sin categoría» al final y sin espinas vacías. */
function espinasDe(nodos: AnalisisNodo[], catalogo: {code: string; label_es: string}[], sinLabel: string) {
  const grupos: EspinaData[] = catalogo
    .map((cat) => ({
      key: cat.code,
      label: cat.label_es,
      causas: nodos.filter((n) => n.espina === cat.code).map((n) => n.descripcion),
    }))
    .filter((g) => g.causas.length > 0)
  const sin = nodos.filter((n) => n.espina === null).map((n) => n.descripcion)
  return sin.length > 0 ? [...grupos, {key: SIN_ESPINA, label: sinLabel, causas: sin}] : grupos
}

function ListaDeCausas({
  nodos,
  numerada,
  editable,
  onQuitar,
  quitando,
}: {
  nodos: AnalisisNodo[]
  numerada: boolean
  editable: boolean
  onQuitar: (id: string) => void
  quitando: boolean
}) {
  const {t} = useTranslation()
  const nivelDe = (nodo: AnalisisNodo) => {
    let nivel = 0
    let actual = nodo
    while (actual.parent_nodo_id) {
      const padre = nodos.find((n) => n.id === actual.parent_nodo_id)
      if (!padre) break
      actual = padre
      nivel += 1
    }
    return nivel
  }
  return (
    <ul className="space-y-1.5">
      {nodos.map((nodo, i) => (
        <li
          key={nodo.id}
          data-testid="analisis-causa-row"
          className="flex items-start gap-2 rounded-lg border border-l-[3px] border-border border-l-primary p-2.5"
          style={{marginLeft: `${nivelDe(nodo) * 16}px`}}>
          <div className="min-w-0 flex-1">
            {numerada && <p className="font-mono text-[10px] text-muted-foreground tabular-nums">{i + 1}</p>}
            <p className="text-sm break-words">{nodo.descripcion}</p>
            {nodo.espina_label && (
              <p className="mt-0.5 font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
                {nodo.espina_label}
              </p>
            )}
          </div>
          {editable && (
            <button
              type="button"
              data-testid="analisis-causa-quitar"
              disabled={quitando}
              onClick={() => onQuitar(nodo.id)}
              className="shrink-0 cursor-pointer font-mono text-sm leading-none text-muted-foreground hover:text-destructive"
              aria-label={t('analisis.quitarCausa')}>
              ×
            </button>
          )}
        </li>
      ))}
    </ul>
  )
}

function CapturaDeCausa({
  businessId,
  actionId,
  analisis,
  categorias,
}: {
  businessId: string
  actionId: string
  analisis: AnalisisCausa
  categorias: {code: string; label_es: string}[]
}) {
  const {t} = useTranslation()
  const agregar = useAgregarCausa(businessId, actionId)
  const [descripcion, setDescripcion] = useState('')
  const [categoria, setCategoria] = useState<CausaCategoria | ''>('')
  const [error, setError] = useState<string | null>(null)
  const esIshikawa = analisis.metodologia === 'ishikawa'
  const esCadena = analisis.metodologia === 'cinco_porques'
  const ultimo = analisis.nodos.length > 0 ? analisis.nodos[analisis.nodos.length - 1] : null

  return (
    <div className="surface-divider space-y-3 rounded-xl border border-dashed bg-secondary/30 p-4">
      <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
        {esCadena && ultimo
          ? t('analisis.captura.siguientePorque', {n: analisis.nodos.length + 1})
          : t('analisis.captura.titulo')}
      </p>
      {esIshikawa && (
        <Campo label={t('analisis.captura.espinaLabel')} opcional>
          <NativeSelect
            data-testid="analisis-espina-select"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value as CausaCategoria | '')}>
            <option value="">{t('analisis.captura.sinEspina')}</option>
            {categorias.map((cat) => (
              <option key={cat.code} value={cat.code}>
                {cat.label_es}
              </option>
            ))}
          </NativeSelect>
        </Campo>
      )}
      <Campo label={t('analisis.captura.causaLabel')}>
        <Textarea
          maxLength={tope('TEXTO')}
          data-testid="analisis-causa-input"
          value={descripcion}
          minRows={2}
          placeholder={t('analisis.captura.placeholder')}
          onChange={(e) => setDescripcion(e.target.value)}
        />
      </Campo>
      <Button
        size="sm"
        className="min-h-11 sm:min-h-0"
        data-testid="analisis-causa-add"
        disabled={descripcion.trim() === '' || agregar.isPending}
        onClick={() => {
          setError(null)
          agregar.mutate(
            {
              descripcion: descripcion.trim(),
              espina: esIshikawa && categoria !== '' ? categoria : null,
              // En 5 porqués cada respuesta cuelga de la anterior: el mismo grafo se lee como escalera.
              parent_nodo_id: esCadena && ultimo ? ultimo.id : null,
            },
            {onSuccess: () => setDescripcion(''), onError: (err) => setError(translateApiError(err))}
          )
        }}>
        {t('analisis.captura.agregar')}
      </Button>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

function AnalisisAbierto({
  businessId,
  actionId,
  analisis,
  problema,
  editable,
}: {
  businessId: string
  actionId: string
  analisis: AnalisisCausa
  problema: string
  editable: boolean
}) {
  const {t} = useTranslation()
  const {data: categorias} = useCategorias(businessId)
  const cambiar = useCambiarMetodologia(businessId, actionId)
  const quitar = useQuitarCausa(businessId, actionId)
  const descartar = useDescartarAnalisis(businessId, actionId)
  const espinas = espinasDe(analisis.nodos, categorias ?? [], t('analisis.sinEspina'))

  return (
    <div className="space-y-4">
      <SegmentedControl
        testId="analisis-metodologia"
        size="sm"
        value={analisis.metodologia}
        onChange={(m) => {
          if (editable && m !== analisis.metodologia) cambiar.mutate(m)
        }}
        ariaLabel={t('analisis.metodologiaAria')}
        options={METODOLOGIAS.map((m) => ({key: m, label: t(`investigacion.metodologia.${m}`)}))}
      />
      {analisis.nodos.length === 0 ? (
        <ListEmptyState title={t('analisis.vacioTitulo')} body={t('analisis.vacioCuerpo')} />
      ) : (
        <>
          {analisis.metodologia === 'ishikawa' && <EspinaDePescado espinas={espinas} problema={problema} />}
          <ListaDeCausas
            nodos={analisis.nodos}
            numerada={analisis.metodologia === 'cinco_porques'}
            editable={editable}
            onQuitar={(id) => quitar.mutate(id)}
            quitando={quitar.isPending}
          />
        </>
      )}
      {editable && (
        <>
          <CapturaDeCausa
            businessId={businessId}
            actionId={actionId}
            analisis={analisis}
            categorias={categorias ?? []}
          />
          <button
            type="button"
            data-testid="analisis-descartar"
            disabled={descartar.isPending}
            onClick={() => descartar.mutate()}
            className="cursor-pointer text-xs text-muted-foreground underline-offset-2 hover:text-destructive hover:underline">
            {t('analisis.descartarAnalisis')}
          </button>
        </>
      )}
    </div>
  )
}

export function AnalisisCausaDialog({
  businessId,
  actionId,
  descripcion,
  editable,
  open,
  onOpenChange,
}: {
  businessId: string
  actionId: string | null
  descripcion: string
  editable: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const {data: analisis, isPending} = useAnalisisCausa(open && actionId ? businessId : undefined, actionId ?? undefined)
  const abrir = useAbrirAnalisis(businessId, actionId ?? '')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-4xl overflow-y-auto" data-testid="analisis-causa-dialog">
        <DialogHeader>
          <DialogTitle>{t('analisis.titulo')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{descripcion}</p>
        {actionId && !isPending && !analisis && (
          <ListEmptyState
            title={t('analisis.abrirTitulo')}
            body={t('analisis.abrirCuerpo')}
            action={
              editable ? (
                <Button
                  size="sm"
                  data-testid="analisis-abrir"
                  disabled={abrir.isPending}
                  onClick={() => abrir.mutate('ishikawa')}>
                  {t('analisis.abrirCta')}
                </Button>
              ) : undefined
            }
          />
        )}
        {actionId && analisis && (
          <AnalisisAbierto
            businessId={businessId}
            actionId={actionId}
            analisis={analisis}
            problema={descripcion}
            editable={editable}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
